import { Phone } from "lucide-react";
import Reveal from "../components/Reveal";
import { trackPhoneClick } from "../lib/analytics";

const US_IMAGE =
    "https://images.pexels.com/photos/14629409/pexels-photo-14629409.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CA_IMAGE =
    "https://images.pexels.com/photos/38333500/pexels-photo-38333500.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const US_POINTS = [
    "EOR services for US companies",
    "Employment administration in supported markets",
    "Employee onboarding and documentation support",
    "Ongoing workforce administration",
];

export default function Markets() {
    return (
        <section
            id="solutions"
            data-testid="markets-section"
            className="bg-white py-24 lg:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="max-w-2xl">
                    <Reveal>
                        <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-royal">
                            <span className="h-px w-10 bg-gold" aria-hidden="true" />
                            Markets We Serve
                        </p>
                        <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                            Focused coverage, done properly
                        </h2>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-5 lg:grid-cols-12">
                    <Reveal className="lg:col-span-7">
                        <article
                            data-testid="us-solutions-card"
                            className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-sm"
                        >
                            <div className="relative h-64 overflow-hidden sm:h-72">
                                <img
                                    src={US_IMAGE}
                                    alt="Skyscrapers in a major United States business district"
                                    loading="lazy"
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                                />
                                <span className="absolute left-5 top-5 rounded-full bg-gold px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.22em] text-navy">
                                    Primary Market
                                </span>
                            </div>
                            <div className="flex flex-1 flex-col p-8 lg:p-10">
                                <h3 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                                    US Business Solutions
                                </h3>
                                <p className="mt-4 text-base leading-relaxed text-navy/65">
                                    The United States is our primary market. We
                                    work with US founders, business owners, and
                                    HR leaders who are evaluating employer of
                                    record arrangements and international
                                    employment administration — whether
                                    engaging talent across states or exploring
                                    supported cross-border hiring.
                                </p>
                                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {US_POINTS.map((point) => (
                                        <li
                                            key={point}
                                            className="flex items-start gap-2.5 text-sm font-medium text-navy/80"
                                        >
                                            <svg
                                                width="8"
                                                height="8"
                                                viewBox="0 0 10 10"
                                                className="mt-1.5 shrink-0"
                                                aria-hidden="true"
                                            >
                                                <path d="M5 0 10 5 5 10 0 5Z" fill="#C8A96B" />
                                            </svg>
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    href="tel:+18333439367"
                                    data-testid="us-solutions-call-cta"
                                    onClick={() => trackPhoneClick("us_solutions")}
                                    className="mt-8 inline-flex w-fit items-center gap-2.5 rounded-full bg-navy px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-[background-color,transform] duration-200 hover:bg-royal hover:scale-[1.02]"
                                >
                                    <Phone size={15} aria-hidden="true" />
                                    Discuss Your Hiring Needs
                                </a>
                            </div>
                        </article>
                    </Reveal>

                    <Reveal delay={0.12} className="lg:col-span-5">
                        <article
                            data-testid="canada-services-card"
                            className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy/10 bg-mist"
                        >
                            <div className="relative h-52 overflow-hidden sm:h-60">
                                <img
                                    src={CA_IMAGE}
                                    alt="Toronto skyline with the CN Tower in monochrome"
                                    loading="lazy"
                                    className="h-full w-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]"
                                />
                                <span className="absolute left-5 top-5 rounded-full border border-navy/20 bg-white/90 px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.22em] text-navy/70 backdrop-blur-sm">
                                    Secondary Market
                                </span>
                            </div>
                            <div className="flex flex-1 flex-col p-8 lg:p-10">
                                <h3 className="font-display text-xl font-bold tracking-tight text-navy sm:text-2xl">
                                    Canada Services
                                </h3>
                                <p className="mt-4 flex-1 text-sm leading-relaxed text-navy/60">
                                    Canada is our secondary market, and service
                                    availability depends on your specific
                                    employment scenario. If your business has
                                    cross-border needs involving Canada, call
                                    our team to confirm how we can support you.
                                </p>
                                <a
                                    href="tel:+18333439367"
                                    data-testid="canada-services-call-cta"
                                    onClick={() => trackPhoneClick("canada_services")}
                                    className="mt-7 inline-flex w-fit items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-royal transition-colors duration-200 hover:text-navy"
                                >
                                    <Phone size={15} aria-hidden="true" />
                                    Ask About Canada
                                </a>
                            </div>
                        </article>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
